import { useState } from "react";
import {
  Box, Typography, Paper, TextField, InputAdornment, Alert, Stack, Button, Chip,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { vendorsApi, type VendorDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { VendorForm } from "./VendorForm";

export function VendorsPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<VendorDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<VendorDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("procurement.vendor.create"));
  const canUpdate = useAuth(s => s.hasPermission("procurement.vendor.edit"));
  const canDelete = useAuth(s => s.hasPermission("procurement.vendor.delete"));
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["vendors", search, pagination.page, pagination.pageSize],
    queryFn: () => vendorsApi.list({
      search: search || undefined,
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vendorsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendors"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (v: VendorDto) => { setEditing(v); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const handleDelete = (v: VendorDto) => {
    closeMenu();
    if (confirm(`Delete vendor "${v.name}"? This is blocked if any POs reference this vendor.`)) {
      deleteMutation.mutate(v.id);
    }
  };

  const columns: GridColDef<VendorDto>[] = [
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "contactPerson", headerName: "Contact", width: 160 },
    { field: "phone", headerName: "Phone", width: 130 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "gstin", headerName: "GSTIN", width: 160 },
    { field: "creditDays", headerName: "Credit days", width: 110, type: "number" },
    {
      field: "isActive", headerName: "Status", width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Vendors</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Vendor
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p: GridRowParams<VendorDto>) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canUpdate && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <VendorForm open={formOpen} onClose={() => setFormOpen(false)} vendor={editing} />
    </Box>
  );
}
